import { useState, useEffect, useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  useCreateProject,
  useUpdateProject,
  useProjects,
} from '../api/useProjects'
import { logger } from '@/lib/utils/logger'
import type { Project } from '@/types/project.types'

interface ProjectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  project?: Project
  mode: 'create' | 'edit'
}

const PROJECT_TYPES = [
  { value: 'platform', label: 'Platform' },
  { value: 'product', label: 'Product' },
  { value: 'service', label: 'Service' },
  { value: 'module', label: 'Module' },
] as const

export function ProjectDialog({
  open,
  onOpenChange,
  project,
  mode,
}: ProjectDialogProps) {
  const [name, setName] = useState('')
  const [prefix, setPrefix] = useState('')
  const [description, setDescription] = useState('')
  const [projectType, setProjectType] = useState<string>('service')
  const [parentId, setParentId] = useState<string>('none')
  const [isPublic, setIsPublic] = useState(false)
  const [color, setColor] = useState('#1976D2')

  const createMutation = useCreateProject()
  const updateMutation = useUpdateProject()
  const { data: allProjects } = useProjects()

  // Flatten projects for parent dropdown, excluding self and descendants in edit mode
  const parentOptions = useMemo(() => {
    if (!allProjects) return []
    if (mode === 'create') return allProjects

    // In edit mode, exclude the project itself and any of its descendants
    const excludeIds = new Set<number>()

    const collectDescendants = (id: number) => {
      excludeIds.add(id)
      for (const p of allProjects) {
        if (p.parent_id === id) collectDescendants(p.id)
      }
    }

    if (project) collectDescendants(project.id)
    return allProjects.filter((p) => !excludeIds.has(p.id))
  }, [allProjects, mode, project])

  // Reset form when dialog opens or project changes
  useEffect(() => {
    if (!open) return

    if (mode === 'edit' && project) {
      setName(project.name)
      setPrefix(project.prefix || '')
      setDescription(project.description || '')
      setProjectType(project.project_type || 'service')
      setParentId(project.parent_id ? project.parent_id.toString() : 'none')
      setIsPublic(project.is_public ?? false)
      setColor(project.color || '#1976D2')
    } else {
      setName('')
      setPrefix('')
      setDescription('')
      setProjectType('service')
      setParentId('none')
      setIsPublic(false)
      setColor('#1976D2')
    }
  }, [open, mode, project])

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Project name is required')
      return
    }
    if (mode === 'create' && !prefix.trim()) {
      toast.error('Project prefix is required')
      return
    }

    const parentIdNum = parentId !== 'none' ? parseInt(parentId, 10) : null

    try {
      if (mode === 'create') {
        await createMutation.mutateAsync({
          name: name.trim(),
          prefix: prefix.trim(),
          description: description.trim() || undefined,
          color,
          is_public: isPublic,
          parent_id: parentIdNum,
          project_type: projectType as 'platform' | 'product' | 'service' | 'module',
        })
        toast.success(`Project "${name.trim()}" created`)
      } else if (project) {
        await updateMutation.mutateAsync({
          id: project.id,
          data: {
            name: name.trim(),
            description: description.trim() || undefined,
            color,
            is_public: isPublic,
            parent_id: parentIdNum,
            project_type: projectType as 'platform' | 'product' | 'service' | 'module',
          },
        })
        toast.success(`Project "${name.trim()}" updated`)
      }
      onOpenChange(false)
    } catch (err) {
      const action = mode === 'create' ? 'create' : 'update'
      logger.error(`Failed to ${action} project`, err)
      toast.error(
        err instanceof Error ? err.message : `Failed to ${action} project`
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create Project' : 'Edit Project'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Set up a new test project to organize your test cases and plans.'
              : `Update settings for ${project?.name}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="proj-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="proj-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Payment Gateway"
              disabled={isSubmitting}
            />
          </div>

          {/* Prefix — only editable on create */}
          <div className="space-y-2">
            <Label htmlFor="proj-prefix">
              Prefix <span className="text-red-500">*</span>
            </Label>
            <Input
              id="proj-prefix"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value.toUpperCase())}
              placeholder="e.g. PG"
              maxLength={50}
              disabled={isSubmitting || mode === 'edit'}
            />
            {mode === 'edit' && (
              <p className="text-xs text-muted-foreground">
                Prefix cannot be changed after creation.
              </p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="proj-desc">Description</Label>
            <Textarea
              id="proj-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the project..."
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Project Type */}
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={projectType}
                onValueChange={setProjectType}
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Color */}
            <div className="space-y-2">
              <Label htmlFor="proj-color">Color</Label>
              <div className="flex items-center gap-2">
                <input
                  id="proj-color"
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-12 rounded border cursor-pointer"
                  disabled={isSubmitting}
                />
                <span className="text-sm text-muted-foreground font-mono">
                  {color}
                </span>
              </div>
            </div>
          </div>

          {/* Parent Project */}
          <div className="space-y-2">
            <Label>Parent Project</Label>
            <Select
              value={parentId}
              onValueChange={setParentId}
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="None (top-level)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (top-level)</SelectItem>
                {parentOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>
                    {p.name} ({p.prefix})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Public toggle */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label>Public Project</Label>
              <p className="text-xs text-muted-foreground">
                Public projects are visible to all organization members.
              </p>
            </div>
            <Switch
              checked={isPublic}
              onCheckedChange={setIsPublic}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-[#1976D2] hover:bg-[#1565C0]"
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'create' ? 'Create Project' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
